/*
 * GA4 measurement topline.
 *
 * The Platform Analytics workspace is source-agnostic: it renders whatever
 * governed payload it is handed. This adapter produces that same payload shape
 * from Google Analytics so the GA4 page can use the canonical composition
 * without the shell learning anything about GA4 — the only thing that differs
 * between a media page and a measurement page is which function fills `raw`.
 *
 * GA4 reports site activity, not media delivery. There is deliberately no
 * spend, CPM, CPC or basis here: those fields being absent is what keeps
 * paid-media concepts off a measurement surface.
 *
 * `missing !== zero` is load-bearing. The NSC property only began collecting
 * in Aug 2026, so most windows are genuinely unobserved; a metric GA4 did not
 * report stays null and renders as an em dash, while a reported 0 is kept
 * because it is an observation.
 */

import { describeAnalysisWindow, resolveDefaultAnalysisTimeframe } from "./analyticsToplineService";

const normalizeBase = (base) => {
  const text = String(base || "").trim();
  if (!text) return "";
  return text.replace(/\/+$/, "");
};

const DEFAULT_PRODUCTION_BACKEND_URL = "https://cphere-backend.onrender.com";
const API_BASE_URL = normalizeBase(
  process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production" ? DEFAULT_PRODUCTION_BACKEND_URL : "")
);

export const GA4_MEASUREMENT_SOURCE = "ga4";

/*
 * Guard the blank/null cases BEFORE Number(): `Number(null)` is 0, not NaN, so
 * a bare coercion turns "GA4 never reported this" into "GA4 reported zero".
 * This repo has shipped that bug twice.
 */
export function toGa4Metric(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function firstDefined(...values) {
  for (const value of values) {
    const metric = toGa4Metric(value);
    if (metric != null) return metric;
  }
  return null;
}

function getAuthHeader() {
  try {
    const token =
      localStorage.getItem("authToken") ||
      localStorage.getItem("clientAuthToken") ||
      localStorage.getItem("superadminToken") ||
      "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function fetchJson(path, { signal } = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
    credentials: "include",
    signal,
    cache: "no-store",
  });
  const text = await response.text();
  let parsed = {};
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch {
    parsed = {};
  }
  if (!response.ok) {
    const error = new Error(parsed?.error || "Google Analytics reporting is unavailable.");
    error.status = response.status;
    throw error;
  }
  return parsed;
}

/*
 * The overview lives at a different depth depending on which governed wrapper
 * answered, so read every shape the existing consumers already accept rather
 * than assuming one.
 */
export function resolveGa4Overview(payload) {
  return (
    payload?.summary?.overview ||
    payload?.overview ||
    payload?.google_analytics?.summary?.overview ||
    null
  );
}


/*
 * The breakdowns sit at different depths depending on which governed wrapper
 * answered, and the shared audience module expects them on one object. Surface
 * them explicitly rather than passing the envelope and hoping the shapes line
 * up — a silent shape mismatch renders as "no breakdowns", which is
 * indistinguishable from Google withholding them.
 */
export function resolveGa4AudienceChannelData(payload) {
  const summary = payload?.summary || payload || {};
  const demographics = payload?.demographics || summary?.demographics || {};
  const pick = (...candidates) => {
    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length) return candidate;
    }
    return [];
  };
  return {
    ageBreakdown: pick(summary.ageBreakdown, payload?.ageBreakdown, demographics.ageBreakdown),
    genderBreakdown: pick(summary.genderBreakdown, payload?.genderBreakdown, demographics.genderBreakdown),
    deviceMix: pick(
      summary.deviceMix,
      payload?.deviceMix,
      summary.deviceBreakdown,
      payload?.deviceBreakdown,
      demographics.deviceMix
    ),
    /*
     * The window overview travels WITH the breakdowns. A segmented card for a
     * rate, an average or a distinct count has no valid aggregate of its own —
     * summing the rows produces a number that means nothing — so it needs
     * GA4's authoritative window figure, which is this same overview the KPI
     * cards print. Carrying it here is what stops the two disagreeing.
     */
    overview: resolveGa4Overview(payload) || null,
    // Google's thresholding verdict is the backend's classification, carried
    // through rather than re-derived from row counts.
    demographicsStatus: payload?.demographicsStatus || summary?.demographicsStatus || null,
  };
}

export function buildGa4MeasurementRaw(overview) {
  const sessions = firstDefined(overview?.sessions);
  const engagedSessions = firstDefined(overview?.engagedSessions, overview?.engaged_sessions);
  const reportedRate = firstDefined(overview?.engagementRate, overview?.engagement_rate);

  /*
   * GA4 returns engagementRate as a 0–1 ratio. Publish it on a percent axis to
   * match every other percent card, but only derive it when GA4 did not report
   * it and both inputs are present — never manufacture a rate from a missing
   * denominator.
   */
  const engagementRatePercent =
    reportedRate != null
      ? reportedRate <= 1
        ? reportedRate * 100
        : reportedRate
      : sessions != null && sessions > 0 && engagedSessions != null
      ? (engagedSessions / sessions) * 100
      : null;

  return {
    ga4Users: firstDefined(overview?.totalUsers, overview?.users, overview?.total_users),
    ga4NewUsers: firstDefined(overview?.newUsers, overview?.new_users),
    ga4Sessions: sessions,
    ga4EngagedSessions: engagedSessions,
    ga4EngagementRate: engagementRatePercent,
    ga4EventCount: firstDefined(overview?.eventCount, overview?.event_count),
    ga4AvgSessionDuration: firstDefined(
      overview?.averageSessionDuration,
      overview?.average_session_duration,
      overview?.avgSessionDuration
    ),
    // Kept so the shared "GA Sessions" metric still resolves if a page asks.
    sessions,
  };
}

/*
 * Whether Google Analytics is actually the source of this payload.
 *
 * The backend already classifies this — `ga4Diagnostics.connected` plus the
 * resolved property id say whether a GA4 property answered at all. Naming the
 * source unconditionally would have made every advertiser without a GA4
 * mapping report "Google Analytics 4" as its reporting source, so the identity
 * is carried from the backend's own verdict rather than from the fact that the
 * GA4 adapter is the code that ran.
 */
export function resolveGa4SourceIdentity(payload) {
  const diagnostics = payload?.ga4Diagnostics || payload?.ga4Status || null;
  const propertyId =
    String(payload?.propertyId || diagnostics?.propertyId || "").trim() || null;
  const connected = diagnostics
    ? diagnostics.connected === true || Boolean(propertyId)
    : Boolean(propertyId);
  return {
    connected,
    propertyId,
    /* The canonical name of this source, or nothing. Never a guess. */
    label: connected ? "Google Analytics 4" : null,
    classification: diagnostics?.classification || null,
    reason: String(diagnostics?.reason || "").trim() || null,
  };
}

export function hasAnyGa4Observation(raw) {
  return Object.entries(raw || {}).some(
    ([key, value]) => key !== "sessions" && value != null
  );
}

/*
 * GA4's daily metric names -> the page's metric keys.
 *
 * These four are exactly the profile's `chartMetricKeys`, which is not a
 * coincidence to preserve by hand: a chart metric that GA4 cannot report by day
 * would have no series to switch to. Engagement Rate and Avg Session Duration
 * are absent on purpose — they are a ratio and an average, they are not
 * selectable on the chart, and a daily ratio drawn on a count axis would misread
 * by orders of magnitude. Their KPI cards stay GA4's own window figures.
 */
export const GA4_DAILY_METRIC_KEYS = Object.freeze({
  sessions: "ga4Sessions",
  totalUsers: "ga4Users",
  newUsers: "ga4NewUsers",
  engagedSessions: "ga4EngagedSessions",
});

/*
 * Distinct-count metrics, named in the page's own vocabulary.
 *
 * GA4 estimates distinct users; one person returning on three days is three
 * daily observations and one window user. So these days are real observations
 * that must NOT be summed — the chart draws them and the KPI card above keeps
 * GA4's authoritative window count. The backend decides this (it owns GA4
 * semantics) and states it on the series; this is the translation of that
 * verdict, never a second opinion about it.
 */
const NON_ADDITIVE_FALLBACK = Object.freeze(["totalUsers", "newUsers"]);

/*
 * The same defensive default for date-overlapping metrics: a session is a span
 * and may touch two calendar days, so a `date`-dimensioned series can total
 * more than the undimensioned window query. Only consulted when the backend
 * declared nothing.
 */
const DATE_OVERLAPPING_FALLBACK = Object.freeze(["sessions", "engagedSessions"]);

const isIsoDay = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));

/*
 * The governed daily series -> the canonical `dailyDelivery` contract the
 * shared Platform Analytics view model already reads for every media platform.
 *
 * Producing the SAME shape is the whole point: the delivery chart, its dated
 * axis, its reconciliation gate and its spoken summary are one implementation,
 * and a measurement source earns dated columns by supplying the evidence they
 * are drawn from rather than by teaching the chart about GA4.
 *
 * A day GA4 did not report is absent, never a zero. A day it reported AS zero
 * keeps its zero, because that one is an observation.
 */
export function buildGa4DailyDelivery(series) {
  const rawDays = Array.isArray(series?.days) ? series.days : [];
  const days = rawDays
    .filter((day) => day && isIsoDay(day.date))
    .map((day) => {
      const source = day.metrics && typeof day.metrics === "object" ? day.metrics : {};
      const metrics = {};
      Object.entries(GA4_DAILY_METRIC_KEYS).forEach(([ga4Name, metricKey]) => {
        const value = toGa4Metric(source[ga4Name]);
        if (value != null) metrics[metricKey] = value;
      });
      return { date: day.date, metrics };
    })
    .filter((day) => Object.keys(day.metrics).length > 0)
    .sort((a, b) => a.date.localeCompare(b.date));

  if (!days.length) return null;

  const declared = Array.isArray(series?.nonAdditiveMetrics)
    ? series.nonAdditiveMetrics
    : NON_ADDITIVE_FALLBACK;
  const overlapping = Array.isArray(series?.dateOverlappingMetrics)
    ? series.dateOverlappingMetrics
    : DATE_OVERLAPPING_FALLBACK;

  return {
    channel: GA4_MEASUREMENT_SOURCE,
    days,
    observedDays: days.length,
    /*
     * GA4 reports a property by day with nothing left over: there is no span of
     * evidence that belongs to no single date, which is what `complete: false`
     * exists to declare for media whose sources report cumulatively.
     */
    complete: true,
    undecomposableChannels: [],
    nonAdditiveMetricKeys: declared
      .map((name) => GA4_DAILY_METRIC_KEYS[name])
      .filter(Boolean),
    /*
     * Summable, but over dates that overlap: a session spanning midnight is
     * counted on both days and once for the window, so these days may total
     * slightly MORE than the figure above them. Carried through so the chart
     * can say that, rather than reading the overshoot as a broken series.
     */
    dateOverlappingMetricKeys: overlapping
      .map((name) => GA4_DAILY_METRIC_KEYS[name])
      .filter(Boolean),
  };
}

/**
 * Returns a payload shaped like the governed topline result, filled from GA4.
 * Signature matches getToplinePerformance so the workspace can swap sources
 * without knowing which one it got.
 */
export async function getGa4MeasurementTopline({
  advertiserId,
  timeframe = null,
  advertiserFlightRange = null,
  signal,
} = {}) {
  const normalizedAdvertiserId = String(advertiserId || "").trim();
  if (!normalizedAdvertiserId) return null;

  const campaignsPayload = await fetchJson(
    `/api/media-campaigns?advertiserId=${encodeURIComponent(normalizedAdvertiserId)}`,
    { signal }
  ).catch(() => ({}));
  const campaigns = Array.isArray(campaignsPayload?.campaigns)
    ? campaignsPayload.campaigns
    : Array.isArray(campaignsPayload)
    ? campaignsPayload
    : [];

  // The same window vocabulary every other Analytics page resolves against, so
  // the GA4 chart and the GA4 cards can never disagree about the period.
  const normalizedTimeframe = resolveDefaultAnalysisTimeframe({
    timeframe,
    campaigns,
    advertiserFlightRange,
  });

  const query = new URLSearchParams({ advertiserId: normalizedAdvertiserId });
  if (normalizedTimeframe?.datePreset) query.set("datePreset", normalizedTimeframe.datePreset);
  if (normalizedTimeframe?.start) query.set("startDate", normalizedTimeframe.start);
  if (normalizedTimeframe?.end) query.set("endDate", normalizedTimeframe.end);

  /*
   * Two governed reads of ONE window: GA4's aggregate for the KPI cards, and
   * GA4's `date`-dimensioned series for the chart beneath them. Both are asked
   * for the same resolved window, so the cards and the columns can never
   * describe different periods.
   *
   * The daily read is allowed to fail on its own. A property that reports a
   * window total but not by day is a real state, and it must degrade to the
   * window-total column rather than take the whole page down with it.
   */
  const dailyQuery = new URLSearchParams(query);
  dailyQuery.delete("advertiserId");
  dailyQuery.set("metrics", Object.keys(GA4_DAILY_METRIC_KEYS).join(","));

  const [summary, dailyPayload] = await Promise.all([
    fetchJson(`/api/google-analytics/summary?${query.toString()}`, { signal }),
    fetchJson(
      `/api/google-analytics/daily-sessions/${encodeURIComponent(
        normalizedAdvertiserId
      )}?${dailyQuery.toString()}`,
      { signal }
    ).catch(() => null),
  ]);
  const overview = resolveGa4Overview(summary);
  const raw = buildGa4MeasurementRaw(overview);
  const dailyDelivery = buildGa4DailyDelivery(dailyPayload?.series);
  const analysisWindow = describeAnalysisWindow(normalizedTimeframe);
  const usable = hasAnyGa4Observation(raw);
  const sourceIdentity = resolveGa4SourceIdentity(summary);

  return {
    scopeLabel: "Google Analytics",
    windowLabel: analysisWindow?.label || null,
    analysisWindow,
    topPlatform: null,
    raw: {
      ...raw,
      platformKey: "ga4",
      metricsSource: GA4_MEASUREMENT_SOURCE,
      datePreset: normalizedTimeframe?.datePreset || null,
      startDate: normalizedTimeframe?.start || null,
      endDate: normalizedTimeframe?.end || null,
      analysisWindowSource: normalizedTimeframe?.source || null,
      analysisWindowMode: analysisWindow?.mode || null,
      analysisWindowSubtext: analysisWindow?.subtext || null,
      // GA4 buckets by the PROPERTY's zone; the server reports which basis it
      // used rather than letting a client assumption stand in for it.
      /*
       * The daily series carries the property's zone in its own GA4 metadata,
       * so it is the last fallback here — consulted only when the summary
       * declared none. Still the server's answer either way; a client
       * assumption never stands in for it.
       */
      propertyTimeZone:
        summary?.propertyTimeZone ||
        summary?.timeZone ||
        dailyPayload?.series?.timeZone ||
        null,
      /*
       * Source IDENTITY, from the backend's connectivity verdict. GA4 answers
       * over its own API rather than out of an ingest snapshot, so it sets no
       * `snapshotIs*` flag and declares no tier, confidence or freshness — the
       * shared readiness contract reads that as a resolved source with an
       * incomplete governance declaration, which is what it is. Nothing is
       * invented here to complete it.
       */
      snapshotSourceLabel: sourceIdentity.label,
      ga4PropertyId: sourceIdentity.propertyId,
      ga4Classification: sourceIdentity.classification,
      snapshotSourceNotes: sourceIdentity.reason ? [sourceIdentity.reason] : [],
      /*
       * The dated evidence, on the canonical key every platform page reads. Its
       * ABSENCE is the honest signal that this property reports only a window
       * total — the chart says so in words and draws one column, which is the
       * state this page was stuck in for every property while nothing filled
       * this key in.
       */
      ...(dailyDelivery ? { dailyDelivery } : {}),
      /*
       * The whole governed summary is carried through so the shared audience
       * module can build device and demographic breakdowns from it — including
       * Google's age/gender thresholding state. Passing the payload rather than
       * re-deriving keeps that classification the backend's decision.
       */
      audienceChannelData: resolveGa4AudienceChannelData(summary),
      hasAnyData: usable,
      usableDataFound: usable,
    },
  };
}

export default getGa4MeasurementTopline;
